import {
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Badge,
	Tooltip,
	CircularProgress,
	Backdrop,
	Box,
	Typography,
	Divider,
} from '@mui/material';
import { Link, useLocation, useParams, useNavigation } from 'react-router';
import { useState, useEffect, useMemo } from 'react';
import {
	PeopleAltOutlined as PeopleAltOutlinedIcon,
	TableChartOutlined as TableViewIcon,
	CableOutlined as CableIcon,
	SchemaOutlined as SchemaOutlinedIcon,
	StorefrontOutlined as StorefrontOutlinedIcon,
	Waves as WavesIcon,
	CalculateOutlined as CalculateIcon,
	SmartToyOutlined as SmartToyIcon,
	SettingsOutlined as SettingsIcon,
	Insights as InsightsIcon,
	LockOutlined as LockIcon,
	SwitchAccountOutlined as SwitchAccountOutlinedIcon,
	EmailOutlined as EmailOutlinedIcon,
	CloudUploadOutlined as CloudUploadOutlinedIcon,
	CellTower as CellTowerIcon,
} from '@mui/icons-material';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import { useTheme } from '@mui/material/styles';
import SidebarSection from './SidebarSection';

import { authedApiFetch } from '../../utils/Api';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

/**
 * Navigation menu for the Hub app
 * @param {Object} props - Component props
 * @param {Object} props.user - The current user object
 * @param {boolean} props.isCollapsed - Whether the sidebar is collapsed
 */
const HubNavigation = ({ tenant, user, isCollapsed }) => {
	const theme = useTheme();
	const { tenantId } = useParams();
	const { pathname } = useLocation();
	const navigation = useNavigation();

	const NAVIGATION_FLAG_KEYS = useMemo(
		() => [
			'show-crm-nav-item',
			'show-vdr-portal-nav-item',
			'show-analytics-nav-item',
			'show-email-sync-nav-item',
			'show-file-upload-nav-item',
			'show-connections-nav-item',
			'show-marketplace-nav-item',
			'show-pricing-nav-item',
			'show-udm-nav-item',
			'show-datalake-nav-item',
			'show-datatable-nav-item',
			'show-users-nav-item',
			'show-aime-config-nav-item',
			'show-tenant-admin-nav-item',
			'show-email-sync-admin-nav-item',
			'enable-ir-workstation',
		],
		[]
	);

	const { flagStatuses, flagsLoading } = useFeatureFlags(tenant, NAVIGATION_FLAG_KEYS);

	// Helper function to check if we're navigating to a specific route
	// Raw loading state check
	const rawIsLoading = navigation.state === 'loading';

	// State for delayed loading indicators (both overlay and menu items)
	const [showDelayedLoading, setShowDelayedLoading] = useState(false);

	// Track which paths are being navigated to
	const rawIsNavigatingTo = (path) => {
		return (
			rawIsLoading &&
			navigation.location &&
			navigation.location.pathname.includes(path)
		);
	};

	// Delayed version of isNavigatingTo that uses the delayed state
	const isNavigatingTo = (path) => {
		return showDelayedLoading && rawIsNavigatingTo(path);
	};

	// Simple effect for delayed loading indicators
	useEffect(() => {
		let timer;
		if (rawIsLoading || flagsLoading) {
			timer = setTimeout(() => setShowDelayedLoading(true), 300);
		} else {
			setShowDelayedLoading(false);
		}
		return () => clearTimeout(timer);
	}, [rawIsLoading, flagsLoading]);

	// handles navigating menu items other than 'vdr-portal'
	const getMenuLinkVal = (item, attribute) => {
		if (item.component === 'vdr-portal') {
			return undefined;
		} else {
			switch (attribute) {
				case 'href':
				case 'to':
					return item.path || undefined;
				case 'target':
					return item.isExternalLink ? '_blank' : undefined;
				default:
					return undefined;
			}
		}
	};

	// handles signing user into the VDR Portal
	// requires a one-time code from the backend before navigation
	const handleVdrPortalLogin = async (e, item) => {
		e.stopPropagation();

		// if component is vdr-portal, generate url with one-time code to sign user into ShareSecure
		if (item.component === 'vdr-portal') {
			let url = new URL(item.path);

			try {
				const response = await authedApiFetch({
					endpoint: '/auth/sso_code',
					method: 'GET',
				});
				const code = response.code || '';

				url.pathname = '/sso';
				url.searchParams.append('code', code);
			} catch (error) {
				console.error(
					'Error generating a one-time code to sign user into the VDR Portal: ',
					error
				);
			}

			window.open(url, '_blank', 'noopener,noreferrer');
		}
	};


	const navItems = () => {
		const sections = {
			launchPad: {
				title: 'LaunchPad',
				items: [
					{
						name: 'CRM',
						icon: <SwitchAccountOutlinedIcon />,
						component: 'crm',
						// TODO: Replace with actual tenant Salesforce URL when available
						path:
							(tenant && tenant.salesforce_org_url) ||
							'https://login.salesforce.com/',
						isExternalLink: true,
						isVisible: flagStatuses['show-crm-nav-item'],
					},
					{
						name: 'VDR/Portal',
						icon: <WebOutlinedIcon />,
						component: 'vdr-portal',
						path: process.env.REACT_APP_SSV2_FRONTEND_URL,
						isExternalLink: true,
						isVisible: flagStatuses['show-vdr-portal-nav-item'],
					},
					{
						name: 'Investor Engagement Manager',
						icon: <CellTowerIcon />,
						component: 'workstation',
						path: `/tenants/${tenantId}/workstation`,
						isVisible: flagStatuses['enable-ir-workstation'],
					},
					{
						name: 'Analytics',
						icon: <InsightsIcon />,
						component: 'analytics',
						path: '#', // Placeholder for future Analytics URL
						isExternalLink: true,
						disabled: true, // Disabled until Analytics is available
						isVisible: flagStatuses['show-analytics-nav-item'],
					},
				],
			},
			mainFeatures: {
				title: 'Features',
				items: [
					{
						name: 'Email Sync',
						icon: <EmailOutlinedIcon />,
						component: 'email-sync',
						path: `/tenants/${tenantId}/email-sync`,
						isVisible:
							user.role !== 'tenant_read_only' &&
							user.tenant_id &&
							flagStatuses['show-email-sync-nav-item'], //["tenant_admin", "tenant_user"]
					},
					{
						name: 'File Upload',
						icon: <CloudUploadOutlinedIcon />,
						component: 'file-upload',
						path: `/tenants/${tenantId}/file-upload`,
						isVisible:
							user.role !== 'tenant_read_only' &&
							flagStatuses['show-file-upload-nav-item'], //["altvia_users", "tenant_admin", "tenant_user"]
					},
					{
						name: 'Connections',
						icon: <CableIcon />,
						component: 'connections',
						path: `/tenants/${tenantId}/connections`,
						isVisible: flagStatuses['show-connections-nav-item'], // Available to all users
					},
				],
			},
			financials: {
				title: 'Subscriptions',
				items: [
					{
						name: 'Marketplace',
						icon: <StorefrontOutlinedIcon />,
						component: 'marketplace',
						path: `/tenants/${tenantId}/marketplace`,
						isVisible: flagStatuses['show-marketplace-nav-item'],
					},
					{
						name: 'Pricing',
						icon: <CalculateIcon />,
						component: 'pricing',
						path: `/tenants/${tenantId}/pricing`,
						isVisible: !user.tenant_id && flagStatuses['show-pricing-nav-item'], // only Altvia users
					},
				],
			},
			dataManagement: {
				title: 'Datalake',
				items: [
					{
						name: 'Universal Data Model',
						icon: <SchemaOutlinedIcon />,
						component: 'udm',
						path: `/tenants/${tenantId}/udm`,
						isVisible: !user.tenant_id && flagStatuses['show-udm-nav-item'], // only Altvia users
					},
					{
						name: 'Lakeflow',
						icon: <WavesIcon />,
						component: 'datalake',
						path: `/tenants/${tenantId}/datalake`,
						isVisible:
							!user.tenant_id && flagStatuses['show-datalake-nav-item'], // only Altvia users
					},
					{
						name: 'Data Tables',
						icon: <TableViewIcon />,
						component: 'data-tables',
						path: `/tenants/${tenantId}/data-tables`,
						isVisible: flagStatuses['show-datatable-nav-item'], // Available to all users
					},
				],
			},
			administration: {
				title: 'Administration',
				items: [
					{
						name: 'Users',
						icon: <PeopleAltOutlinedIcon />,
						component: 'users',
						path: `/tenants/${tenantId}/users`,
						isVisible: flagStatuses['show-users-nav-item'], // Available to all users
					},
					{
						name: 'AIMe Config',
						icon: <SmartToyIcon />,
						component: 'aime-config',
						path: `/tenants/${tenantId}/aime-config`,
						isVisible:
							(!user.tenant_id || user.role == 'tenant_admin') &&
							flagStatuses['show-aime-config-nav-item'], // available to Altvia users and tenant admins
					},
					{
						name: 'Tenant Admin',
						icon: <SettingsIcon />,
						component: 'admin',
						path: `/tenants/${tenantId}/admin`,
						isVisible:
							(user.role === 'altvia_admin' ||
								user.role === 'altvia_power_user') &&
							flagStatuses['show-tenant-admin-nav-item'], // Only Altvia admin users
					},
					{
						name: 'Email Sync Admin',
						icon: <SyncAltRoundedIcon />,
						component: 'email-sync-admin',
						path: `/tenants/${tenantId}/email-sync-admin`,
						isVisible:
							(user.role === 'altvia_admin' ||
								user.role === 'altvia_power_user' ||
								user.role === 'tenant_admin') &&
							flagStatuses['show-email-sync-admin-nav-item'], // Only Altvia admin and tenant admin users
					},
				],
			},
		};

		// Return sections that have at least one displayed item
		return Object.values(sections).filter(
			(section) => section.items.some(item => item.isVisible)
		);
	}

	// Determine selected menu item by flattening all items from all sections
	const findSelectedItem = () => {
		const allItems = navItems()
			.flatMap((section) => section.items)
			.filter((item) => !item.isExternalLink); // Only consider internal links for selection

		// Sort by path length descending - longest paths first
		// checks for 2 similar paths 'email-sync' and 'email-sync-admin'
		const sortedItems = allItems.sort((a, b) => b.path.length - a.path.length);

		const selectedItem = sortedItems.find((item) =>
			pathname.includes(item.path)
		);

		return selectedItem ? selectedItem.component : null;
	};

	const selected = findSelectedItem();

	// Render the menu with sections
	const renderMenuWithSections = () => {
		const sections = navItems();

		return sections.map((section, index) => (
			<Box key={`section-${index}`}>
				{index > 0 && <Divider sx={{ my: 1 }} />}
				<SidebarSection title={section.title} isCollapsed={isCollapsed} />
				{section.items.map((item) => (
					<Tooltip
						title={isCollapsed ? item.name : ''}
						placement='right'
						key={`tooltip-${item.component}`}
					>
						{
							item.isVisible && (
								<ListItemButton
									key={item.component}
									selected={!item.isExternalLink && selected === item.component}
									component={item.isExternalLink ? 'a' : Link}
									href={getMenuLinkVal(item, 'href')}
									to={getMenuLinkVal(item, 'to')}
									target={getMenuLinkVal(item, 'target')}
									onClick={(e) => handleVdrPortalLogin(e, item)}
									disabled={item.disabled}
									sx={{
										justifyContent: isCollapsed ? 'center' : 'flex-start',
										py: isCollapsed ? 1.5 : 1,
										minHeight: 48,
										'&.Mui-selected': {
											backgroundColor: theme.palette.primary.main,
											color: theme.palette.primary.contrastText,
											'&:hover': {
												backgroundColor: theme.palette.primary.main,
											},
											'& .MuiListItemIcon-root': {
												color: theme.palette.primary.contrastText,
											},
											'& .MuiListItemText-primary': {
												color: theme.palette.primary.contrastText,
											},
										},
										'& .MuiListItemIcon-root': {
											color:
												selected === item.component
													? theme.palette.primary.contrastText
													: theme.palette.primary.main,
											minWidth: isCollapsed ? 0 : 24,
											mr: isCollapsed ? 0 : 2,
										},
									}}
								>
									<ListItemIcon>
										{item.disabled ? (
											<Box sx={{ position: 'relative', display: 'inline-flex' }}>
												{item.icon}
												<Box
													sx={{
														position: 'absolute',
														bottom: -4,
														right: -4,
														color: theme.palette.text.secondary,
													}}
												>
													<LockIcon sx={{ fontSize: 14 }} />
												</Box>
											</Box>
										) : isNavigatingTo(item.path) && !item.isExternalLink ? (
											<CircularProgress
												size={24}
												color={selected === item.component ? 'inherit' : 'primary'}
											/>
										) : (
											item.icon
										)}
									</ListItemIcon>
									{!isCollapsed && (
										<Box
											sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}
										>
											<ListItemText
												primary={item.name}
												secondary={isNavigatingTo(item.path) ? 'Loading...' : null}
												sx={{
													color:
														selected === item.component
															? theme.palette.primary.contrastText
															: theme.palette.text.primary,
												}}
											/>
											{item.isExternalLink && !item.disabled && (
												<LaunchOutlinedIcon
													color='primary'
													size='small'
													sx={{ fontSize: 16, ml: 1 }}
												/>
											)}
										</Box>
									)}
									{!isCollapsed && item.badge > 0 && (
										<Badge badgeContent={item.badge} color='error' />
									)}
								</ListItemButton>
							)
						}
					</Tooltip>
				))}
			</Box>
		));
	};

	return (
		<>
			{/* Loading overlay */}
			<Backdrop
				open={showDelayedLoading}
				sx={{
					color: '#fff',
					zIndex: (theme) => theme.zIndex.drawer + 1,
					backgroundColor: 'rgba(0, 0, 0, 0.3)',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						bgcolor: 'background.paper',
						p: 3,
						borderRadius: 1,
						boxShadow: 3,
						backgroundColor: theme.palette.background.paper,
					}}
				>
					<CircularProgress color='primary' sx={{ mb: 1 }} />
					<Typography variant='body1'>Loading...</Typography>
				</Box>
			</Backdrop>

			<List>{renderMenuWithSections()}</List>
		</>
	);
};

export default HubNavigation;
